import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import ImageUpload from "../../components/ImageUpload";

const blank = { name: "", breed: "", specialty: "", years_of_service: "", certification: "", description: "", photo: "", microchip: "", for_sale: false, price: "" };

export default function DogsTab({ onViewTraining }) {
  const [dogs, setDogs] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/dogs").then((r) => setDogs(r.data)).catch(() => setDogs([]));
  };
  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setDialogOpen(true);
  };

  const openEdit = (dog) => {
    setEditing(dog);
    setForm({ ...blank, ...dog });
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/dogs/${editing.id}`, form);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/dogs", form);
        toast.success(`${form.name} added to the team`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save dog", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    try {
      await api.delete(`/dogs/${id}`);
      setDogs((prev) => prev.filter((d) => d.id !== id));
      toast.success(`${name} removed`);
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  const [ctCounts, setCtCounts] = useState({});
  useEffect(() => {
    api
      .get("/ct-records")
      .then((r) => {
        const counts = {};
        (r.data || []).forEach((rec) => {
          if (rec.dog_id) counts[rec.dog_id] = (counts[rec.dog_id] || 0) + 1;
        });
        setCtCounts(counts);
      })
      .catch(() => setCtCounts({}));
  }, []);

  if (dogs === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="dogs-loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="dogs-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-white/50">{dogs.length} dog{dogs.length === 1 ? "" : "s"} on the team</p>
        <Button onClick={openNew} data-testid="admin-add-dog-button" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> Add Dog
        </Button>
      </div>

      {dogs.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="dogs-admin-empty">
          No dogs yet. Add your first K9 profile.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {dogs.map((dog) => (
            <div key={dog.id} className="rounded-lg border border-brand-line bg-brand-surface overflow-hidden hover:border-brand-crimson/50 transition-colors" data-testid={`admin-dog-card-${dog.id}`}>
              <div className="h-40 overflow-hidden bg-brand-obsidian">
                {dog.photo ? (
                  <img src={dog.photo} alt={dog.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full flex items-center justify-center text-white/30 text-xs">No photo</div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-display font-bold text-white">{dog.name}</p>
                  {dog.for_sale && (
                    <span className="text-[9px] font-eyebrow uppercase tracking-[0.2em] font-bold bg-brand-crimson text-white px-2 py-1 rounded-full" data-testid={`admin-dog-forsale-${dog.id}`}>
                      For Sale{dog.price ? ` ${dog.price}` : ""}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/50 mt-0.5">{dog.breed} • {dog.specialty}</p>
                <div className="mt-2">
                  <button
                    onClick={() => onViewTraining?.(dog.id)}
                    data-testid={`dog-ct-count-${dog.id}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-eyebrow uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full border transition-all duration-200 ${
                      (ctCounts[dog.id] || 0) > 0
                        ? "text-brand-crimson bg-brand-crimson/10 border-brand-crimson/30 hover:bg-brand-crimson hover:text-white"
                        : "text-white/40 bg-white/5 border-brand-line hover:text-white"
                    }`}
                  >
                    <GraduationCap className="h-3 w-3" />
                    {(ctCounts[dog.id] || 0) > 0 ? `${ctCounts[dog.id]} CT record${ctCounts[dog.id] === 1 ? "" : "s"} - view` : "View training"}
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => onViewTraining?.(dog.id)} data-testid={`admin-dog-training-${dog.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold py-2 rounded-md border border-brand-line text-brand-crimson hover:border-brand-crimson/60 hover:bg-brand-crimson/10 transition-colors">
                    <GraduationCap className="h-3.5 w-3.5" /> Training
                  </button>
                  <button onClick={() => openEdit(dog)} data-testid={`admin-dog-edit-${dog.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold py-2 rounded-md border border-brand-line text-white/70 hover:text-white hover:border-brand-crimson/60 transition-colors">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(dog.id, dog.name)} data-testid={`admin-dog-delete-${dog.id}`} className="p-2 rounded-md border border-brand-line text-white/40 hover:text-red-400 hover:border-red-500/40 transition-colors" aria-label={`Delete ${dog.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dog-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? `Edit ${editing.name}` : "Add a Dog"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload value={form.photo} onChange={(url) => setField("photo", url)} testid="dog-photo-upload" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Name *</Label>
                <Input data-testid="dog-form-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Buster" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Breed</Label>
                <Input data-testid="dog-form-breed" value={form.breed} onChange={(e) => setField("breed", e.target.value)} placeholder="English Springer Spaniel" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Specialty</Label>
                <Input data-testid="dog-form-specialty" value={form.specialty} onChange={(e) => setField("specialty", e.target.value)} placeholder="Knife & Weapon Detection" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Experience</Label>
                <Input data-testid="dog-form-experience" value={form.years_of_service} onChange={(e) => setField("years_of_service", e.target.value)} placeholder="5 Years Active Service" className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Certification</Label>
              <Input data-testid="dog-form-certification" value={form.certification} onChange={(e) => setField("certification", e.target.value)} placeholder="NASDU Level 3 Accredited" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs flex items-center gap-1.5">
                Microchip No <span className="text-[9px] text-white/40 font-normal">(admin only - never shown on the public site)</span>
              </Label>
              <Input data-testid="dog-form-microchip" value={form.microchip} onChange={(e) => setField("microchip", e.target.value)} placeholder="15-digit microchip number" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Bio</Label>
              <Textarea data-testid="dog-form-description" value={form.description} onChange={(e) => setField("description", e.target.value)} rows={4} placeholder="What makes this dog special..." className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="flex flex-wrap items-center gap-6 rounded-md border border-brand-line bg-brand-obsidian p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/80">List For Sale</span>
                <Switch checked={!!form.for_sale} onCheckedChange={(v) => setField("for_sale", v)} data-testid="dog-form-for-sale" />
              </div>
              {form.for_sale && (
                <div className="space-y-1.5 flex-1 min-w-[160px]">
                  <Label className="text-white/70 text-xs">Price</Label>
                  <Input data-testid="dog-form-price" value={form.price || ""} onChange={(e) => setField("price", e.target.value)} placeholder="£1,850" className="bg-brand-obsidian border-brand-line" />
                </div>
              )}
            </div>
            <Button onClick={save} disabled={busy} data-testid="dog-save-button" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
              {busy ? "Saving..." : editing ? "Save Changes" : "Add to Team"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
